import { useTranslation } from 'react-i18next'

import { Box, Divider, Stack, SxProps, Theme, Typography } from '@mui/material'

import responsiveHook from '@hooks/useResponsive'

import { TLGTypography } from '@components/TLGTypography'

import { scaleUpTransition } from '@utils/styles'

import {
  accelerateAuthenticTechConnectionLeftSideContentDescriptionSx,
  accelerateAuthenticTechConnectionLeftSideContentSx,
  accelerateAuthenticTechConnectionLeftSideWrapperSx,
  accelerateAuthenticTechConnectionRightSideImageSx,
  accelerateAuthenticTechConnectionRightSideWrapperSx,
  containerSx,
  contentGridSx,
  dividerWrapperSx,
  itsAboutYouLeftSideContentDescriptionSx,
  itsAboutYouLeftSideContentSx,
  itsAboutYouLeftSideWrapperSx,
  itsAboutYouRightSideImageSx,
  itsAboutYouRightSideWrapperSx,
  knewHadToBeBetterWayLeftSideImageSx,
  knewHadToBeBetterWayLeftSideWrapperSx,
  knewHadToBeBetterWayRightSideContentDescriptionSx,
  knewHadToBeBetterWayRightSideContentSx,
  knewHadToBeBetterWayRightSideWrapperSx,
  theBestPartRightSideContentDescriptionSx,
  theBestPartRightSideContentSx,
  theBestPartRightSideImageSx,
  theBestPartRightSideWrapperSx,
  theBestSideLeftSideWrapperSx,
  titleSx,
} from './styles'

const Content1Section = () => {
  const { t } = useTranslation()

  const { isDesktop, ifDesktop } = responsiveHook.useResponsive()

  return (
    <Box data-testid="about-content-1-section" sx={containerSx}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={contentGridSx}
      >
        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={accelerateAuthenticTechConnectionLeftSideWrapperSx}
        >
          <Stack sx={accelerateAuthenticTechConnectionLeftSideContentSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t(
                'Accelerate Authentic Tech Connections with TechnologyMatch.com: Powered By The Launchpad'
              )}
            </TLGTypography>
            <Stack
              sx={accelerateAuthenticTechConnectionLeftSideContentDescriptionSx}
            >
              <Typography>
                {t(
                  'Our story began two decades ago, fueled by a passion to redefine the way Tech Customers and technology partners connect. As an agency, we listened intently to the pain points and frustrations of both parties. We heard their struggles, their yearning for an elevated tech buying journey, and their desire for meaningful, authentic connections and purpose-driven relationships.'
                )}
              </Typography>
              <Typography>
                {t(
                  'For years, we served as trusted advisors, guiding countless Tech Customers through the maze of tech options and helping technology providers understand the needs of their target audience. We witnessed firsthand the limitations of traditional lead generation campaigns – stifling progress and hindering the potential for meaningful connections.'
                )}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={accelerateAuthenticTechConnectionRightSideWrapperSx}
        >
          <Box
            alt={t('Astronaut seated in a rocket')}
            loading="lazy"
            component="img"
            src="/images/astronaut-seated-in-a-rocket.webp"
            sx={
              {
                ...accelerateAuthenticTechConnectionRightSideImageSx,
                ...ifDesktop(scaleUpTransition),
              } as SxProps<Theme>
            }
          />
        </Box>

        {isDesktop && (
          <Box gridColumn="span 12" sx={dividerWrapperSx}>
            <Divider />
          </Box>
        )}

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={knewHadToBeBetterWayLeftSideWrapperSx}
        >
          <Box
            alt={t('Astronaut on a computer')}
            loading="lazy"
            component="img"
            src="/images/astronaut-on-computer.webp"
            sx={
              {
                ...knewHadToBeBetterWayLeftSideImageSx,
                ...ifDesktop(scaleUpTransition),
              } as SxProps<Theme>
            }
          />
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={knewHadToBeBetterWayRightSideWrapperSx}
        >
          <Stack sx={knewHadToBeBetterWayRightSideContentSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t('And we knew there had to be a better way')}
            </TLGTypography>
            <Stack sx={knewHadToBeBetterWayRightSideContentDescriptionSx}>
              <Typography>
                {t(
                  "Armed with invaluable insights, we set out to create TechnologyMatch.com – an innovation-driven SaaS marketplace that would revolutionize tech marketing and sales. We took the pain points we've heard for years and transformed them into a platform designed to address the specific needs of Tech Customers and technology providers alike."
                )}
              </Typography>
              <Typography>
                {t(
                  'We understand the frustration of Tech Customers drowning in a sea of unsolicited sales calls and outreach from technology providers, when all they want is to find the right solution or partner. And we empathize with technology providers who offer valuable service and create amazing products, only to be held back by outdated lead generation methods.'
                )}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {isDesktop && (
          <Box gridColumn="span 12" sx={dividerWrapperSx}>
            <Divider />
          </Box>
        )}

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={itsAboutYouLeftSideWrapperSx}
        >
          <Stack sx={itsAboutYouLeftSideContentSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t("But it's not about us, it's about you")}
            </TLGTypography>
            <Stack sx={itsAboutYouLeftSideContentDescriptionSx}>
              <Typography>
                {t(
                  "That's why we've built a marketplace that puts you – the Tech Customer and the technology vendor – at the center of the stage. TechnologyMatch.com is not just a platform, but a launchpad for your success. We've taken your feedback, your pain points, and your desires, and we've woven them into the very fabric of our matchmaking marketplace."
                )}
              </Typography>
              <Typography>
                {t(
                  'For Tech Customers, it serves as a sanctuary, where you can take control of your tech discovery and buying journey. No more being bombarded by impersonal sales calls or drowning in a sea of generic pitches. We empower you to self-serve, research, and vet technology partners on your own terms. The power is in your hands to find the perfect match, to discover solutions that meet your unique needs.'
                )}
              </Typography>
              <Typography>
                {t(
                  "And for technology partners, we've created an affordable alternative to outdated methods, where you can unlock a world of targeted, actionable leads. No more wasted time pursuing lukewarm prospects. Our marketplace connects you with Tech Customers actively seeking what you offer."
                )}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={itsAboutYouRightSideWrapperSx}
        >
          <Box
            alt={t('Astronaut waiving hand')}
            loading="lazy"
            component="img"
            src="/images/astronaut-waving-hand.webp"
            sx={{
              ...itsAboutYouRightSideImageSx,
              ...ifDesktop(scaleUpTransition),
            }}
          />
        </Box>

        {isDesktop && (
          <Box gridColumn="span 12" sx={dividerWrapperSx}>
            <Divider />
          </Box>
        )}

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={theBestSideLeftSideWrapperSx}
        >
          <Box
            alt={t('Astronauts talking')}
            loading="lazy"
            component="img"
            src="/images/astronauts-talking.webp"
            sx={{
              ...theBestPartRightSideImageSx,
              ...ifDesktop(scaleUpTransition),
            }}
          />
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={theBestPartRightSideWrapperSx}
        >
          <Stack sx={theBestPartRightSideContentSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t("But here's the best part – our pricing")}
            </TLGTypography>
            <Stack sx={theBestPartRightSideContentDescriptionSx}>
              <Typography>
                {t(
                  "For Tech Customers, it’s free! And we've made it incredibly affordable for technology partners, with a monthly fee that’s a fraction of the cost of traditional lead generation campaigns. We believe that your investment should yield tangible results, not drain your resources"
                )}
              </Typography>
              <Typography>
                {t(
                  "So, whether you're a Tech Customer looking for a better way to find the right technology solutions or a technology partner seeking targeted, qualified leads, our B2B tech matchmaking marketplace is the answer. We've heard your pains, we've witnessed your struggles, and we've crafted a marketplace that addresses them."
                )}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}

export { Content1Section }
